<template>
  <div class="checkbox" :class="[`checkbox--${theme}`, `checkbox--${variant}`]">
    <input
      type="checkbox"
      :checked="value"
      :id="idText"
      :name="nameText"
      :disabled="isDisabled"
      :required="isRequired"
      class="checkbox__field"
      v-bind="$attrs"
      v-on="$listeners"
      @change="$emit('update:value', $event.target.checked)"
      v-data-test-id="'input'"
    />
    <label :for="idText" class="checkbox__label">{{ label }}</label>
    <slot name="below"></slot>
  </div>
</template>

<script>
import { getHash } from '@/helpers/number'
import propsCheckbox from '@/components/_shared/Form/Checkbox/props-checkbox'

export default {
  name: 'Checkbox',

  // bind attributes to input (not to rool el) using v-bind="$attrs"
  inheritAttrs: false,

  props: { ...propsCheckbox },

  data() {
    return {
      hash: getHash(),
    }
  },

  computed: {
    idText() {
      return this.id || `${this.nameText}-${this.hash}`
    },

    nameText() {
      return this.label.replace(/\s+/g, '-').toLowerCase()
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/components/_shared/Form/form-settings';

.checkbox {
  $checkbox-width: space(4);

  &__field {
    /* hide default */
    &:not(:checked),
    &:checked {
      left: -9999px;
      position: absolute;
    }

    /* label */
    &:not(:checked) + label,
    &:checked + label {
      cursor: pointer;
      display: inline-block;
      padding-left: $checkbox-width + 10px;
      position: relative;
      outline: 0;
    }

    /* box */
    &:not(:checked) + label:before,
    &:checked + label:before {
      content: '';
      width: $checkbox-width;
      height: $checkbox-width;
      position: absolute;
      top: 0;
      left: 0;
      background: $color-input-bg;
      border: 1px solid $color-grey-light-5;
      border-radius: 2px;
      outline: 0;
      transition: border-color $transition-time;
    }

    &:focus + label:before {
      border-color: $color-grey-mid-2;
    }

    /* check */
    &:not(:checked) + label:after,
    &:checked + label:after {
      position: absolute;
      top: 3px;
      left: 3px;
      content: '\e60a'; /* icon-tick */
      font-family: 'icomoon';
      font-size: 8px;
      color: $color-black;
      line-height: 1.2;
      transition: all $transition-time;
    }

    &:not(:checked) + label:after {
      opacity: 0;
      transform: scale(0);
    }

    &:checked + label:after {
      opacity: 1;
      transform: scale(1);
    }

    /* disabled */
    &:disabled:not(:checked) + label:before,
    &:disabled:checked + label:before {
      border: 1px solid $color-grey-mid-1;
    }

    &:disabled:checked + label:after {
      color: $color-grey-mid-1;
    }

    &:disabled + label {
      cursor: not-allowed;
      color: $color-grey-mid-1;
    }
  }

  &--red {
    .checkbox__field {
      /* box */
      &:not(:checked) + label:before,
      &:checked + label:before {
        border-color: $field-color-red;
      }
    }
  }

  &--orange {
    .checkbox__field {
      /* box */
      &:not(:checked) + label:before,
      &:checked + label:before {
        border-color: $field-color-orange;
      }
    }
  }

  &--dark {
    .checkbox__field {
      /* box */
      &:not(:checked) + label:before,
      &:checked + label:before {
        background: $color-black;
        border: 1px solid $color-white;
      }

      /* check */
      &:not(:checked) + label:after,
      &:checked + label:after {
        color: $color-white;
      }
    }
  }
}
</style>
